import React from 'react';
import {
  TemplatePreviewData,
  formatTemplateName,
  hasTemplatePreviewContent,
} from '@/lib/templatePreview';

export interface TemplatePreviewPanelProps {
  preview: TemplatePreviewData;
  showTemplateName?: boolean;
  showSectionLabel?: boolean;
  scrollable?: boolean;
  /** When true, renders only preview content (no outer Plantilla container). */
  embedded?: boolean;
  className?: string;
  emptyMessage?: string;
  loading?: boolean;
}

/**
 * Campaign-only template preview panel (markup copied from MessageTemplates TemplateCard).
 */
const TemplatePreviewPanel: React.FC<TemplatePreviewPanelProps> = ({
  preview,
  showTemplateName = true,
  showSectionLabel = true,
  scrollable = true,
  embedded = false,
  className = '',
  emptyMessage = 'Sin plantilla asociada',
  loading = false,
}) => {
  const scrollClass = scrollable && !embedded ? 'max-h-56 overflow-y-auto' : '';
  const hasContent = hasTemplatePreviewContent(preview);

  const inner = (
    <>
      {loading ? (
        <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
          <div className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
          Cargando...
        </div>
      ) : hasContent ? (
        <div className="mt-1">
          {showTemplateName && preview.name && (
            <div className="text-sm font-semibold text-gray-900 mb-1">
              {formatTemplateName(preview.name) || preview.name}
            </div>
          )}

          {preview.headerType === 'IMAGE' && preview.headerImageUrl ? (
            <img
              src={preview.headerImageUrl}
              alt="Header de plantilla"
              className="w-full h-24 object-cover rounded-lg mt-1"
            />
          ) : preview.headerText ? (
            <div className="text-sm font-semibold text-gray-800 mt-1 whitespace-pre-wrap">
              {preview.headerText}
            </div>
          ) : null}

          {preview.bodyText && (
            <div className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">
              {preview.bodyText}
            </div>
          )}

          {preview.footerText && (
            <div className="text-xs text-gray-500 mt-2 italic whitespace-pre-wrap">
              {preview.footerText}
            </div>
          )}

          {preview.buttons && preview.buttons.length > 0 && (
            <div className="flex flex-wrap justify-center gap-1.5 mt-2 pt-2 border-t border-gray-200">
              {preview.buttons.map((btn, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center px-2 py-1 bg-[#E8F7F7] text-[#0D7377] rounded text-xs border border-[#1CB3B3]/30"
                >
                  {btn.text}
                </span>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="text-xs text-gray-500 mt-1">{emptyMessage}</div>
      )}
    </>
  );

  if (embedded) {
    return <div className={className}>{inner}</div>;
  }

  return (
    <div
      className={`mb-4 p-3 bg-[#FAFBFC] border border-gray-200 rounded-xl flex-grow ${scrollClass} ${className}`.trim()}
    >
      {showSectionLabel && (
        <div className="text-xs font-medium text-gray-500 uppercase pb-1">Plantilla</div>
      )}
      {inner}
    </div>
  );
};

export default TemplatePreviewPanel;
